import { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { useApolloClient } from '@apollo/client/react';
import { ME_QUERY, type MeQueryResult } from '@scio3/graphql-client';
import { getFreshToken } from '@/lib/firebase';
import { STORAGE_KEY } from '@/lib/i18n';
import { fullLogout } from '@/lib/logout';
import { SCIO1_STATE_MAP, SCIO1_STATE_TO_PATH } from './scio1-route-map';
import { notifyNavigation } from '@/lib/iframe-child-bridge';
import { useScio1ModalStore } from '@/stores/scio1-modal-store';

// Runtime override: E2E tests set localStorage.__e2e_scio1_url to use a dedicated scio1 instance
const SCIO1_URL =
  (typeof localStorage !== 'undefined' && localStorage.getItem('__e2e_scio1_url')) ||
  import.meta.env.VITE_SCIO1_URL ||
  'http://localhost:3000';
const SCIO1_ORIGIN =
  (typeof localStorage !== 'undefined' && localStorage.getItem('__e2e_scio1_origin')) ||
  import.meta.env.VITE_SCIO1_ORIGIN ||
  (() => {
    try {
      return new URL(SCIO1_URL).origin;
    } catch {
      return SCIO1_URL;
    }
  })();

// Debug: verify scio1 URL resolution (only in E2E builds)
if (import.meta.env.VITE_E2E) {
  console.warn('[Scio1Frame] SCIO1_URL:', SCIO1_URL, '| SCIO1_ORIGIN:', SCIO1_ORIGIN);
}

// Kill switch (default OFF): when VITE_DISABLE_SCIO1=true the scio1 iframe is never mounted —
// page mode renders a native fallback, modal mode renders nothing. Used to test scio3 standalone
// as features migrate off scio1. Off by default = zero behavior change.
// See claude-dev/2026-06-09-scio1-decouple/.
const DISABLE_SCIO1 = import.meta.env.VITE_DISABLE_SCIO1 === 'true';

/** Native placeholder shown (page mode) when scio1 loading is disabled via VITE_DISABLE_SCIO1. */
function Scio1DisabledFallback() {
  const { t } = useTranslation();
  return (
    <div
      data-testid="scio1-disabled-fallback"
      className="flex h-full w-full flex-col items-center justify-center gap-2 bg-background p-12 text-center"
    >
      <p className="text-sm font-medium text-foreground">{t('This page is not available yet')}</p>
      <p className="text-sm text-muted-foreground">
        {t('It is being migrated to the new experience.')}
      </p>
    </div>
  );
}

interface Scio1FrameProps {
  /** 'page' renders the per-route iframe; 'modal' renders the persistent hidden modal adapter */
  mode?: 'page' | 'modal';
  /** scio1 ui-router state name to navigate to after auth. If omitted, derived from current route. */
  targetState?: string;
  /** CSS class for the iframe element (page mode only) */
  className?: string;
  /** Skip SYNC_AUTH on init — used by share link context which authenticates via SYNC_SHARE_AUTH instead */
  skipSyncAuth?: boolean;
}

export function Scio1Frame({
  mode = 'page',
  targetState,
  className,
  skipSyncAuth = false,
}: Scio1FrameProps = {}) {
  const isModalMode = mode === 'modal';

  const token = useAuthStore((s) => s.token);
  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const isEmbedded = useAuthStore((s) => s.isEmbedded);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const client = useApolloClient();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Track handshake state
  const [isReady, setIsReady] = useState(false);
  const pendingRequestsRef = useRef<Map<string, (data: unknown) => void>>(new Map());

  // Page-mode only refs
  const lastRedirectedStateRef = useRef<string | null>(null);
  const scio1NavigatedRef = useRef(false);

  // Page-mode: derive target state from route
  const scio1Route = location.pathname.replace('/app/', '') || 'screenManagement';
  const resolvedState = targetState || SCIO1_STATE_MAP[scio1Route];

  // Parse URL query params to forward to scio1 via $state.go(stateName, params)
  // Memoized on location.search string to avoid unnecessary re-renders
  const queryParams = useMemo(
    () => Object.fromEntries(new URLSearchParams(location.search)),
    [location.search],
  );

  // Modal-mode: store integration
  const { modalVisible, registerBridge, unregisterBridge } = useScio1ModalStore();

  const postToIframe = useCallback((data: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage(JSON.stringify(data), SCIO1_ORIGIN);
  }, []);

  /** Send SYNC_AUTH and wait for responsePromise */
  const syncAuth = useCallback((): Promise<boolean> => {
    if (!token || !firebaseUser) return Promise.resolve(false);

    const requestId = `${isModalMode ? 'adapter_' : ''}sync_${Date.now()}`;
    return new Promise<boolean>((resolve) => {
      const timeout = setTimeout(() => {
        pendingRequestsRef.current.delete(requestId);
        resolve(false);
      }, 10000);

      pendingRequestsRef.current.set(requestId, (data) => {
        clearTimeout(timeout);
        resolve(!!data);
      });

      postToIframe({
        event: 'SYNC_AUTH',
        requestId,
        data: { token, uid: firebaseUser.uid, teamId: activeTeamId },
      });
    });
  }, [isModalMode, token, firebaseUser, activeTeamId, postToIframe]);

  /** (Page mode) Navigate iframe to a scio1 state via ROUTER_REDIRECT */
  const redirectToState = useCallback(
    (stateName: string, params: Record<string, string> = {}) => {
      lastRedirectedStateRef.current = `${stateName}?${new URLSearchParams(params).toString()}`;
      postToIframe({
        event: 'ROUTER_REDIRECT',
        data: { url: stateName, params },
      });
    },
    [postToIframe],
  );

  /** (Modal mode) Send a postMessage and return a Promise resolved by responsePromise */
  const sendWithPromise = useCallback(
    <T = unknown,>(event: string, data: unknown, timeoutMs = 300000): Promise<T> => {
      const requestId = `modal_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      return new Promise<T>((resolve, reject) => {
        const timeout = setTimeout(() => {
          pendingRequestsRef.current.delete(requestId);
          reject(new Error(`scio1 modal timeout for "${event}"`));
        }, timeoutMs);

        pendingRequestsRef.current.set(requestId, (responseData) => {
          clearTimeout(timeout);
          resolve(responseData as T);
        });

        postToIframe({ event, data, requestId });
      });
    },
    [postToIframe],
  );

  // (Modal mode) Register the bridge function in the store so hooks can use it
  useEffect(() => {
    if (!isModalMode) return;
    if (isReady) {
      registerBridge(sendWithPromise, postToIframe);
    }
    return () => {
      if (isModalMode) unregisterBridge();
    };
  }, [isModalMode, isReady, sendWithPromise, postToIframe, registerBridge, unregisterBridge]);

  // (Page mode) When SCIO 3.0 is embedded inside SCIO 1.0, delegate to parent via ROUTER_REDIRECT
  useEffect(() => {
    if (isModalMode) return;
    if (!isEmbedded) return;
    notifyNavigation(location.pathname + location.search);
  }, [isModalMode, isEmbedded, location.pathname, location.search]);

  // Handle messages FROM scio1 iframe
  useEffect(() => {
    if (isEmbedded) return;

    const handler = async (event: MessageEvent) => {
      if (event.origin !== SCIO1_ORIGIN) return;
      // Only process messages from our own iframe ref
      if (event.source !== iframeRef.current?.contentWindow) return;

      let msg: Record<string, unknown>;
      try {
        msg = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }

      const eventName = (msg.event || msg.action) as string;
      const requestId = msg.requestId as string | undefined;
      const data = msg.data as Record<string, unknown> | undefined;

      switch (eventName) {
        case 'ON_INIT':
        case 'init': {
          // eslint-disable-next-line no-console
          if (import.meta.env.VITE_E2E) console.log('scio3 receive IS_IFRAME', new Date());
          postToIframe({ event: 'IS_IFRAME' });

          // Share link context skips SYNC_AUTH — uses SYNC_SHARE_AUTH instead
          // (anonymous users don't have Meteor user records, so _silentSignIn would fail)
          let authOk = false;
          if (!skipSyncAuth) {
            authOk = await syncAuth();
            // Page mode: retry once if syncAuth timed out (iframe may not have been
            // fully ready to receive messages despite sending ON_INIT).
            // Skip retry if token/user is missing — syncAuth returns false immediately
            // in that case and retrying won't help.
            if (!isModalMode && !authOk && token && firebaseUser) {
              if (import.meta.env.VITE_E2E)
                // eslint-disable-next-line no-console
                console.log('scio3 resync receive IS_IFRAME', new Date());
              authOk = await syncAuth();
            }
          }
          if (import.meta.env.VITE_E2E)
            // eslint-disable-next-line no-console
            console.log(
              `scio3 resync receive authOk: ${authOk} | resolvedState: ${resolvedState} | isModalMode: ${isModalMode} | skipSyncAuth: ${skipSyncAuth}`,
              new Date(),
            );
          // Page mode: after auth, ensure scio1 is on the correct team then navigate.
          // scio1's syncAuth skips _switchTeam when the same user is already logged in
          // (Meteor persistent auth), so we explicitly send CHANGE_TEAM to force the switch.
          if (!isModalMode && authOk) {
            if (activeTeamId) {
              postToIframe({ event: 'CHANGE_TEAM', data: { teamId: activeTeamId } });
            }
            if (resolvedState) {
              if (import.meta.env.VITE_E2E)
                // eslint-disable-next-line no-console
                console.log('scio3 resync receive redirectToState', resolvedState, new Date());
              redirectToState(resolvedState, queryParams);
            }
          } else if (isModalMode) {
            if (!skipSyncAuth) {
              // Modal mode also needs CHANGE_TEAM — scio1's syncAuth skips _switchTeam
              // when the same user is already logged in, so without this the modal
              // creates assets on the default team instead of the active one.
              if (activeTeamId) {
                postToIframe({ event: 'CHANGE_TEAM', data: { teamId: activeTeamId } });
              }
              redirectToState('app.adapterPortalSecond');
            }
            // When skipSyncAuth (share context), skip redirect — SYNC_SHARE_AUTH handles auth
          }
          setIsReady(true);
          break;
        }

        case 'ON_GET_TOKEN_FIREBASE':
          try {
            const forceRefresh = (data?.forceRefresh as boolean) ?? false;
            const fresh = await getFreshToken(forceRefresh);
            postToIframe({
              event: 'responsePromise',
              requestId,
              data: {
                token: fresh.token,
                expires: fresh.expires,
                isFBToken: true,
                uid: firebaseUser?.uid,
              },
            });
          } catch (err) {
            if (!isModalMode) console.error('Token refresh failed:', err);
            postToIframe({ event: 'responsePromise', requestId, data: {} });
          }
          break;

        case 'GET_TEAM':
          postToIframe({
            event: 'responsePromise',
            requestId,
            data: { teamId: activeTeamId },
          });
          break;

        // --- Page-mode only events ---
        case 'NAVIGATE':
        case 'ROUTER_REDIRECT': {
          if (isModalMode) break;
          const path = data?.path as string | undefined;
          if (path) {
            scio1NavigatedRef.current = true;
            navigate(path.startsWith('/') ? path : `/app/${path}`);
          }
          break;
        }

        // CALLBACK_URL: scio1 sends { name, url, params } when it navigates internally.
        // Translate the scio1 state name back to a scio3 path so the URL stays in sync.
        // No isModalMode guard — the modal Scio1Frame (AppShell) can receive this event
        // when the page iframe's scio1 navigates (e.g. Outstanding Invoices → Payment Settings).
        case 'CALLBACK_URL': {
          const callbackPath =
            (data?.path as string | undefined) ??
            (data?.name ? SCIO1_STATE_TO_PATH[data.name as string] : undefined);
          if (callbackPath) {
            const basePath = callbackPath.startsWith('/') ? callbackPath : `/app/${callbackPath}`;
            // Skip if scio3 URL already matches — this is an echo from scio1 confirming
            // a navigation that scio3 initiated (e.g. sidebar click). Only navigate when
            // scio1 changed route on its own (e.g. Outstanding Invoices → Payment Settings).
            const currentBase = location.pathname;
            if (currentBase === basePath) break;

            const params = data?.params as Record<string, string> | undefined;
            const search = params ? `?${new URLSearchParams(params).toString()}` : '';
            const fullPath = `${basePath}${search}`;
            // Update lastRedirectedStateRef so the redirect effect knows the iframe's
            // actual state. Without this, navigating back to the previous page would be
            // skipped by the stateKey === lastRedirectedStateRef guard.
            if (data?.name) {
              lastRedirectedStateRef.current = `${data.name}?${new URLSearchParams(params ?? {}).toString()}`;
            }
            scio1NavigatedRef.current = true;
            navigate(fullPath);
          }
          break;
        }

        case 'LOGOUT':
          // scio1 requests logout — full cleanup then navigate to login
          fullLogout(client).then(() => navigate('/signIn'));
          break;

        case 'FOCUS_LOGOUT':
          // scio1 detected session lost on focus — full cleanup + hard reload
          fullLogout(client).then(() => window.location.reload());
          break;

        case 'CHANGE_LANGUAGE': {
          if (isModalMode) break;
          const lang = data?.language as string | undefined;
          if (lang && lang !== i18n.language) {
            i18n.changeLanguage(lang);
            localStorage.setItem(STORAGE_KEY, lang);
          }
          break;
        }

        case 'CLOSE_ADAPTER':
        case 'IS_CLOSE_SUBSCRIPTION_MODAL':
          if (isModalMode) {
            // Guard: when scio1 receives OPEN_APP, it calls $uibModalStack.dismissAll()
            // BEFORE opening the new dialog. Dismissing the old dialog fires CLOSE_ADAPTER
            // back to scio3, which would immediately hide the new overlay. Ignore stale
            // CLOSE_ADAPTER events that arrive within 1s of showModal() (#591 follow-up).
            const { _showTime } = useScio1ModalStore.getState();
            if (Date.now() - _showTime < 1000) break;
            useScio1ModalStore.getState().hideModal();
            // Resolve pending sendWithPromise requests so the modal can be reopened.
            // Without this, the promise hangs for 5 min and subsequent opens fail (#718).
            // Use asset from CLOSE_ADAPTER data when available — scio1 closeAdapter()
            // sends both CLOSE_ADAPTER({asset}) and responsePromise(requestId, asset).
            // CLOSE_ADAPTER often arrives first; resolving with { close: true } would
            // discard the actual data before responsePromise can deliver it (#968).
            const closedAsset = (data as Record<string, unknown> | undefined)?.asset;
            const resolveValue = closedAsset ?? { close: true };
            pendingRequestsRef.current.forEach((resolver) => resolver(resolveValue));
            pendingRequestsRef.current.clear();
            // scio1 saves via Meteor/DDP — Apollo cache doesn't know about new/updated
            // entities. Refetch active queries so lists update without page reload.
            client.refetchQueries({
              include: ['Assets', 'AssetGroups', 'Playlists', 'PlaylistGroups'],
            });
          } else {
            // Page mode: user may have paid — re-check account status.
            // ME_QUERY refetch updates auth-store.isAccountInactive via setMe().
            // If now active, ProtectedRoute re-renders the normal app.
            client
              .query<MeQueryResult>({
                query: ME_QUERY,
                fetchPolicy: 'network-only',
                errorPolicy: 'all',
              })
              .then((result) => {
                if (result.data?.me) {
                  useAuthStore.getState().setMe(result.data.me);
                  useTeamStore.getState().setTeams(result.data.me.teams ?? []);
                }
              });
          }
          break;

        // --- Shared ---
        case 'responsePromise': {
          if (requestId) {
            const resolver = pendingRequestsRef.current.get(requestId);
            if (resolver) {
              pendingRequestsRef.current.delete(requestId);
              resolver(data);
            }
          }
          break;
        }
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [
    isEmbedded,
    isModalMode,
    token,
    firebaseUser,
    activeTeamId,
    i18n,
    navigate,
    client,
    postToIframe,
    syncAuth,
    resolvedState,
    queryParams,
    redirectToState,
    skipSyncAuth,
    location.pathname,
  ]);

  // (Page mode) When targetState changes, redirect without reloading iframe
  useEffect(() => {
    if (isModalMode || isEmbedded) return;
    if (!isReady || !resolvedState) return;

    // Skip if scio1 initiated this navigation (avoid echo)
    if (scio1NavigatedRef.current) {
      scio1NavigatedRef.current = false;
      return;
    }

    // Skip if already on this state with same params
    const stateKey = `${resolvedState}?${new URLSearchParams(queryParams).toString()}`;
    if (lastRedirectedStateRef.current === stateKey) return;

    redirectToState(resolvedState, queryParams);
  }, [isModalMode, isEmbedded, isReady, resolvedState, queryParams, redirectToState]);

  // Re-sync auth when token or team changes
  useEffect(() => {
    if (isEmbedded) return;
    if (!isReady || !token || !firebaseUser) return;

    if (isModalMode) {
      syncAuth();
      // Also send CHANGE_TEAM — syncAuth alone doesn't switch teams in scio1
      // when the user is already logged in (Meteor persistent auth).
      if (activeTeamId) {
        postToIframe({ event: 'CHANGE_TEAM', data: { teamId: activeTeamId } });
      }
    } else {
      syncAuth().then((ok) => {
        if (ok && resolvedState) {
          redirectToState(resolvedState, queryParams);
        }
      });
    }
    // Only re-sync on token/team changes, not on resolvedState changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmbedded, isModalMode, token, activeTeamId]);

  // (Page mode) Sync language to iframe when i18n language changes
  useEffect(() => {
    if (isModalMode || isEmbedded) return;
    postToIframe({
      event: 'SYNC_LANGUAGE',
      data: { language: i18n.language },
    });
  }, [isModalMode, isEmbedded, i18n.language, postToIframe]);

  // When embedded, don't render the iframe
  if (isEmbedded) return null;

  // Kill switch: scio1 loading disabled — never mount the iframe.
  // Modal mode renders nothing; page mode shows a native fallback instead.
  if (DISABLE_SCIO1) return isModalMode ? null : <Scio1DisabledFallback />;

  // Modal mode needs adapterPortalSecond (Angular modal system for OPEN_APP, OPEN_APP_EDIT, etc.)
  // Share link context uses share adapter route (no auth resolve needed)
  // Page mode uses a lighter empty page — only needs postMessage bridge for auth + navigation
  const iframeSrc = isModalMode
    ? skipSyncAuth
      ? `${SCIO1_URL}/share/adapterPortalSecond`
      : `${SCIO1_URL}/app/adapterPortalSecond`
    : `${SCIO1_URL}/meteor-empty-page`;

  if (isModalMode) {
    // Portal to body so the iframe sits as a sibling to Radix dialog portals.
    // Radix sets pointer-events:none on <body> when a dialog is open;
    // pointer-events-auto on the iframe overrides that so clicks reach it.
    return createPortal(
      <iframe
        ref={iframeRef}
        id="scio1-modal-adapter"
        src={iframeSrc}
        title="OptiSigns Modal Adapter"
        className={
          modalVisible
            ? 'pointer-events-auto fixed inset-0 z-[9999] h-full w-full border-0 bg-black/50'
            : 'pointer-events-none fixed left-0 top-0 h-0 w-0 opacity-0'
        }
        allow="clipboard-write"
      />,
      document.body,
    );
  }

  return (
    <>
      {!isReady && !className?.includes('hidden') && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        className={className || 'h-full w-full border-0'}
        title="OptiSigns"
        allow="clipboard-write; camera; microphone"
        id="iframe-scio"
      />
    </>
  );
}
